import { Code, Download, Copy, Check, Eye, EyeOff, Workflow, Zap, Globe, Shield, ShieldCheck, Database, Radio } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useEnhancedToast } from '@/components/ui/enhanced-toast';
import { useState } from 'react';
import { motion } from 'framer-motion';
import type { WidgetResponse } from '@/api/widgets/widgets.types';

interface WidgetIntegrationGuideProps {
    widgetData: WidgetResponse;
}

export function WidgetIntegrationGuide({ widgetData }: WidgetIntegrationGuideProps) {
    const { success } = useEnhancedToast();
    const [isHmacCopied, setIsHmacCopied] = useState(false);
    const [isHmacVisible, setIsHmacVisible] = useState(false);

    // Generate simple integration code
    const getSimpleIntegrationCode = () => {
        return `<script src="https://cdn.chat.voicebot.studio/widget.js"
    data-chatbot-id="${widgetData.chatbotId}">
</script>`;
    };

    // Generate headless integration code (basic version for display)
    const getHeadlessIntegrationCode = () => {
        return `<!DOCTYPE html>
<html lang="en">

<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Simple Headless Widget Example</title>
    <style>
        body {
            font-family: Arial, sans-serif;
            margin: 20px;
        }

        .container {
            max-width: 800px;
            margin: 0 auto;
        }

        .section {
            margin: 20px 0;
            padding: 15px;
            border: 1px solid #ddd;
        }

        .messages {
            height: 200px;
            overflow-y: auto;
            border: 1px solid #ccc;
            padding: 10px;
            margin: 10px 0;
        }

        .message {
            margin: 5px 0;
            padding: 5px;
        }

        .user {
            background: #e3f2fd;
            text-align: right;
        }

        .bot {
            background: #f3e5f5;
        }

        .system {
            background: #fff3e0;
            font-style: italic;
        }

        input,
        textarea,
        button {
            margin: 5px;
            padding: 8px;
        }

        button {
            background: #007cba;
            color: white;
            border: none;
            cursor: pointer;
        }

        button:disabled {
            background: #ccc;
            cursor: not-allowed;
        }

        .status {
            font-weight: bold;
        }

        .active {
            color: green;
        }

        .inactive {
            color: red;
        }

        .widget-info {
            background: #f8f9fa;
            padding: 8px;
            border-radius: 4px;
            border: 1px solid #e9ecef;
        }
    </style>
</head>

<body>
    <div class="container">
        <h1>Headless Widget Example</h1>
        <p>Minimal code showing all widget methods</p>

        <!-- Connection Status & Widget Info -->
        <div class="section">
            <h3>Connection Status & Widget Info</h3>
            <div id="status" class="status inactive">Connecting...</div>
            <div id="widgetInfo" class="widget-info" style="margin-top: 10px; font-size: 14px; line-height: 1.4;">Not
                connected</div>
        </div>

        <!-- Chat Section -->
        <div class="section">
            <h3>💬 Chat Functions</h3>
            <div id="messages" class="messages">
                <div class="message system">Welcome! Widget loading...</div>
            </div>
            <div>
                <input type="text" id="messageInput" placeholder="Type message..." style="width: 70%;">
                <button onclick="sendMessage()" id="sendBtn" disabled>Send</button>
            </div>
            <div>
                <button onclick="getStoredMessages()">Get Messages</button>
                <button onclick="addCustomMessage()">Add Test Message</button>
                <button onclick="clearHistory()">Clear History</button>
            </div>
        </div>

        <!-- Voice Section -->
        <div class="section">
            <h3>🎤 Voice Functions</h3>
            <div>
                <strong>Call Status:</strong> <span id="voiceStatus" class="inactive">Inactive</span><br>
                <strong>Duration:</strong> <span id="duration">0s</span><br>
                <strong>Muted:</strong> <span id="muteStatus">No</span>
            </div>
            <div style="margin: 10px 0;">
                Volume: <span id="volumeLevel">█████░░░░░</span>
            </div>
            <div>
                <button onclick="startVoice()" id="startBtn">Start Voice Call</button>
                <button onclick="endVoice()" id="endBtn" disabled>End Call</button>
                <button onclick="toggleMute()" id="muteBtn" disabled>Toggle Mute</button>
            </div>
        </div>

    </div>

    <!-- Load Widget (Async) -->
    <script>
    (function() {
        if (window.VoicebotSDK) return; // Prevent duplicates
        
        const script = document.createElement('script');
        script.src = 'https://cdn.chat.voicebot.studio/widget.js';
        script.setAttribute('data-chatbot-id', '${widgetData.chatbotId}');
        script.setAttribute('data-headless', 'true');
        script.async = true;
        
        // Load when DOM is ready
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', () => {
                document.head.appendChild(script);
            });
        } else {
            document.head.appendChild(script);
        }
    })();
    </script>

    <script>
        let widget = null;

        // === HELPER FUNCTIONS ===
        function addMessage(text, type = 'user') {
            const div = document.createElement('div');
            div.className = \`message \${type}\`;
            div.textContent = text;
            document.getElementById('messages').appendChild(div);
            document.getElementById('messages').scrollTop = document.getElementById('messages').scrollHeight;
        }

        function updateStatus(connected) {
            const status = document.getElementById('status');
            status.textContent = connected ? 'Connected' : 'Disconnected';
            status.className = \`status \${connected ? 'active' : 'inactive'}\`;
            document.getElementById('sendBtn').disabled = !connected;
        }

        function updateVoiceStatus() {
            if (!widget) return;

            const isActive = widget.isVoiceCallActive();
            const isMuted = widget.isVoiceMuted();
            const duration = widget.getVoiceCallDuration() || 0;

            document.getElementById('voiceStatus').textContent = isActive ? 'Active' : 'Inactive';
            document.getElementById('voiceStatus').className = isActive ? 'active' : 'inactive';
            document.getElementById('duration').textContent = \`\${duration}s\`;
            document.getElementById('muteStatus').textContent = isMuted ? 'Yes' : 'No';

            document.getElementById('startBtn').disabled = isActive;
            document.getElementById('endBtn').disabled = !isActive;
            document.getElementById('muteBtn').disabled = !isActive;
        }

        function updateVolumeDisplay(level) {
            const bars = Math.floor(level);
            const display = '█'.repeat(bars) + '░'.repeat(10 - bars);
            document.getElementById('volumeLevel').textContent = display;
        }

        // === CHAT FUNCTIONS ===
        async function sendMessage() {
            if (!widget) return;

            const input = document.getElementById('messageInput');
            const text = input.value.trim();
            if (!text) return;

            addMessage(text, 'user');
            input.value = '';

            try {
                await widget.send(text);
            } catch (error) {
                addMessage(\`Error: \${error.message}\`, 'system');
            }
        }

        function getStoredMessages() {
            if (!widget) return;

            const messages = widget.getMessages();
            addMessage(\`Found \${messages.length} stored messages\`, 'system');

            if (messages.length > 0) {
                const latest = messages[messages.length - 1];
                addMessage(\`Latest: "\${latest.text}" (\${latest.sender})\`, 'system');
            }
        }

        function addCustomMessage() {
            if (!widget) return;

            const message = widget.addMessage('Custom test message', 'user');
            addMessage(\`Added message with ID: \${message.id}\`, 'system');
        }

        function clearHistory() {
            if (!widget) return;

            widget.clearHistory();
            addMessage('Chat history cleared', 'system');
        }

        // === VOICE FUNCTIONS ===
        async function startVoice() {
            if (!widget) return;

            try {
                // FIX: Grant microphone consent before starting call
                localStorage.setItem('voicebot-microphone-consent', 'granted');
                localStorage.setItem('voicebot-microphone-consent-date', new Date().toISOString());
                
                await widget.startVoiceCall();
            } catch (error) {
                addMessage(\`Voice failed: \${error.message}\`, 'system');
            }
        }

        async function endVoice() {
            if (!widget) return;

            try {
                await widget.endVoiceCall();
            } catch (error) {
                addMessage(\`End call failed: \${error.message}\`, 'system');
            }
        }

        function toggleMute() {
            if (!widget) return;

            const wasMuted = widget.isVoiceMuted();
            widget.setVoiceMuted(!wasMuted);
            addMessage(wasMuted ? 'Unmuted' : 'Muted', 'system');
            updateVoiceStatus();
        }

        // === WIDGET CONNECTION ===
        function connectWidget() {
            if (window.VoicebotSDK && !widget) {
                widget = window.VoicebotSDK;

                try {
                    // Set up event listeners
                    widget.on('message', (data) => {
                        const text = data.message || data.response || 'Response received';
                        addMessage(text, 'bot');
                        updateWidgetInfo(); // Update info after each message (session ID may be set)
                    });

                    widget.on('voice-started', () => {
                        addMessage('Voice call started', 'system');
                        updateVoiceStatus();
                    });

                    widget.on('voice-ended', () => {
                        addMessage('Voice call ended', 'system');
                        updateVoiceStatus();
                        updateVolumeDisplay(0);
                    });

                    widget.on('voice-error', (data) => {
                        addMessage(\`Voice error: \${data.error || 'Unknown'}\`, 'system');
                    });

                    // FIX: Listen for microphone consent required event
                    widget.on('microphone-consent-required', () => {
                        addMessage('Microphone permission required - granting automatically...', 'system');
                        localStorage.setItem('voicebot-microphone-consent', 'granted');
                        localStorage.setItem('voicebot-microphone-consent-date', new Date().toISOString());
                        // Retry the call
                        setTimeout(() => widget.startVoiceCall(), 100);
                    });

                    // Volume monitoring
                    if (widget.voiceManager) {
                        widget.voiceManager.on('volume-level', (level) => {
                            updateVolumeDisplay(Math.floor(level * 10));
                        });
                    }

                    // Update widget info
                    function updateWidgetInfo() {
                        const sessionId = widget.getSessionId();
                        const info = \`
                            Chatbot ID: \${widget.getChatbotId()}<br>
                            Anonymous ID: \${widget.getAnonId()}<br>
                            Session ID: \${sessionId || 'Loading...'}<br>
                            Headless: \${widget.isHeadless()}<br>
                            Voice Enabled: \${widget.voiceManager ? 'Yes' : 'No'}
                        \`;
                        document.getElementById('widgetInfo').innerHTML = info;
                    }

                    updateWidgetInfo();

                    updateStatus(true);
                    updateVoiceStatus();
                    addMessage('Widget connected successfully!', 'system');

                    // Wait a bit for bootstrap to complete, then update info again
                    setTimeout(updateWidgetInfo, 500);

                } catch (error) {
                    addMessage(\`Connection failed: \${error.message}\`, 'system');
                    updateStatus(false);
                }
            }
        }

        // === INITIALIZATION ===
        // Enter key support
        document.getElementById('messageInput').addEventListener('keypress', (e) => {
            if (e.key === 'Enter') {
                sendMessage();
            }
        });

        // Try to connect every second until successful
        const connectInterval = setInterval(() => {
            if (!widget) {
                connectWidget();
            } else {
                clearInterval(connectInterval);
            }
        }, 1000);

        // Update voice status every second
        setInterval(() => {
            if (widget) updateVoiceStatus();
        }, 1000);

        // Initial setup
        updateStatus(false);
    </script>
</body>

</html>`;
    };

    // Generate complete HTML file content
    const generateCompleteHTMLFile = (type: 'simple' | 'headless') => {
        if (type === 'simple') {
            return `<!DOCTYPE html>
<html lang="en">

<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>VoiceBot Chat Widget Example</title>

</head>

<body>
    <script src="https://cdn.chat.voicebot.studio/widget.js"
        data-chatbot-id="${widgetData.chatbotId}">
    </script>
</body>

</html>`;
        }

        // Complete headless example with your comprehensive implementation
        return `<!DOCTYPE html>
<html lang="en">

<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Simple Headless Widget Example</title>
    <style>
        body {
            font-family: Arial, sans-serif;
            margin: 20px;
        }

        .container {
            max-width: 800px;
            margin: 0 auto;
        }

        .section {
            margin: 20px 0;
            padding: 15px;
            border: 1px solid #ddd;
        }

        .messages {
            height: 200px;
            overflow-y: auto;
            border: 1px solid #ccc;
            padding: 10px;
            margin: 10px 0;
        }

        .message {
            margin: 5px 0;
            padding: 5px;
        }

        .user {
            background: #e3f2fd;
            text-align: right;
        }

        .bot {
            background: #f3e5f5;
        }

        .system {
            background: #fff3e0;
            font-style: italic;
        }

        input,
        textarea,
        button {
            margin: 5px;
            padding: 8px;
        }

        button {
            background: #007cba;
            color: white;
            border: none;
            cursor: pointer;
        }

        button:disabled {
            background: #ccc;
            cursor: not-allowed;
        }

        .status {
            font-weight: bold;
        }

        .active {
            color: green;
        }

        .inactive {
            color: red;
        }

        .widget-info {
            background: #f8f9fa;
            padding: 8px;
            border-radius: 4px;
            border: 1px solid #e9ecef;
        }
    </style>
</head>

<body>
    <div class="container">
        <h1>Headless Widget Example</h1>
        <p>Minimal code showing all widget methods</p>

        <!-- Connection Status & Widget Info -->
        <div class="section">
            <h3>Connection Status & Widget Info</h3>
            <div id="status" class="status inactive">Connecting...</div>
            <div id="widgetInfo" class="widget-info" style="margin-top: 10px; font-size: 14px; line-height: 1.4;">Not
                connected</div>
        </div>

        <!-- Chat Section -->
        <div class="section">
            <h3>💬 Chat Functions</h3>
            <div id="messages" class="messages">
                <div class="message system">Welcome! Widget loading...</div>
            </div>
            <div>
                <input type="text" id="messageInput" placeholder="Type message..." style="width: 70%;">
                <button onclick="sendMessage()" id="sendBtn" disabled>Send</button>
            </div>
            <div>
                <button onclick="getStoredMessages()">Get Messages</button>
                <button onclick="addCustomMessage()">Add Test Message</button>
                <button onclick="clearHistory()">Clear History</button>
            </div>
        </div>

        <!-- Voice Section -->
        <div class="section">
            <h3>🎤 Voice Functions</h3>
            <div>
                <strong>Call Status:</strong> <span id="voiceStatus" class="inactive">Inactive</span><br>
                <strong>Duration:</strong> <span id="duration">0s</span><br>
                <strong>Muted:</strong> <span id="muteStatus">No</span>
            </div>
            <div style="margin: 10px 0;">
                Volume: <span id="volumeLevel">█████░░░░░</span>
            </div>
            <div>
                <button onclick="startVoice()" id="startBtn">Start Voice Call</button>
                <button onclick="endVoice()" id="endBtn" disabled>End Call</button>
                <button onclick="toggleMute()" id="muteBtn" disabled>Toggle Mute</button>
            </div>
        </div>

    </div>

    <!-- Load Widget (Async) -->
    <script>
    (function() {
        if (window.VoicebotSDK) return; // Prevent duplicates
        
        const script = document.createElement('script');
        script.src = 'https://cdn.chat.voicebot.studio/widget.js';
        script.setAttribute('data-chatbot-id', '${widgetData.chatbotId}');
        script.setAttribute('data-headless', 'true');
        script.async = true;
        
        // Load when DOM is ready
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', () => {
                document.head.appendChild(script);
            });
        } else {
            document.head.appendChild(script);
        }
    })();
    </script>

    <script>
        let widget = null;

        // === HELPER FUNCTIONS ===
        function addMessage(text, type = 'user') {
            const div = document.createElement('div');
            div.className = \`message \${type}\`;
            div.textContent = text;
            document.getElementById('messages').appendChild(div);
            document.getElementById('messages').scrollTop = document.getElementById('messages').scrollHeight;
        }

        function updateStatus(connected) {
            const status = document.getElementById('status');
            status.textContent = connected ? 'Connected' : 'Disconnected';
            status.className = \`status \${connected ? 'active' : 'inactive'}\`;
            document.getElementById('sendBtn').disabled = !connected;
        }

        function updateVoiceStatus() {
            if (!widget) return;

            const isActive = widget.isVoiceCallActive();
            const isMuted = widget.isVoiceMuted();
            const duration = widget.getVoiceCallDuration() || 0;

            document.getElementById('voiceStatus').textContent = isActive ? 'Active' : 'Inactive';
            document.getElementById('voiceStatus').className = isActive ? 'active' : 'inactive';
            document.getElementById('duration').textContent = \`\${duration}s\`;
            document.getElementById('muteStatus').textContent = isMuted ? 'Yes' : 'No';

            document.getElementById('startBtn').disabled = isActive;
            document.getElementById('endBtn').disabled = !isActive;
            document.getElementById('muteBtn').disabled = !isActive;
        }

        function updateVolumeDisplay(level) {
            const bars = Math.floor(level);
            const display = '█'.repeat(bars) + '░'.repeat(10 - bars);
            document.getElementById('volumeLevel').textContent = display;
        }

        // === CHAT FUNCTIONS ===
        async function sendMessage() {
            if (!widget) return;

            const input = document.getElementById('messageInput');
            const text = input.value.trim();
            if (!text) return;

            addMessage(text, 'user');
            input.value = '';

            try {
                await widget.send(text);
            } catch (error) {
                addMessage(\`Error: \${error.message}\`, 'system');
            }
        }

        function getStoredMessages() {
            if (!widget) return;

            const messages = widget.getMessages();
            addMessage(\`Found \${messages.length} stored messages\`, 'system');

            if (messages.length > 0) {
                const latest = messages[messages.length - 1];
                addMessage(\`Latest: "\${latest.text}" (\${latest.sender})\`, 'system');
            }
        }

        function addCustomMessage() {
            if (!widget) return;

            const message = widget.addMessage('Custom test message', 'user');
            addMessage(\`Added message with ID: \${message.id}\`, 'system');
        }

        function clearHistory() {
            if (!widget) return;

            widget.clearHistory();
            addMessage('Chat history cleared', 'system');
        }

        // === VOICE FUNCTIONS ===
        async function startVoice() {
            if (!widget) return;

            try {
                // FIX: Grant microphone consent before starting call
                localStorage.setItem('voicebot-microphone-consent', 'granted');
                localStorage.setItem('voicebot-microphone-consent-date', new Date().toISOString());
                
                await widget.startVoiceCall();
            } catch (error) {
                addMessage(\`Voice failed: \${error.message}\`, 'system');
            }
        }

        async function endVoice() {
            if (!widget) return;

            try {
                await widget.endVoiceCall();
            } catch (error) {
                addMessage(\`End call failed: \${error.message}\`, 'system');
            }
        }

        function toggleMute() {
            if (!widget) return;

            const wasMuted = widget.isVoiceMuted();
            widget.setVoiceMuted(!wasMuted);
            addMessage(wasMuted ? 'Unmuted' : 'Muted', 'system');
            updateVoiceStatus();
        }

        // === WIDGET CONNECTION ===
        function connectWidget() {
            if (window.VoicebotSDK && !widget) {
                widget = window.VoicebotSDK;

                try {
                    // Set up event listeners
                    widget.on('message', (data) => {
                        const text = data.message || data.response || 'Response received';
                        addMessage(text, 'bot');
                        updateWidgetInfo(); // Update info after each message (session ID may be set)
                    });

                    widget.on('voice-started', () => {
                        addMessage('Voice call started', 'system');
                        updateVoiceStatus();
                    });

                    widget.on('voice-ended', () => {
                        addMessage('Voice call ended', 'system');
                        updateVoiceStatus();
                        updateVolumeDisplay(0);
                    });

                    widget.on('voice-error', (data) => {
                        addMessage(\`Voice error: \${data.error || 'Unknown'}\`, 'system');
                    });

                    // FIX: Listen for microphone consent required event
                    widget.on('microphone-consent-required', () => {
                        addMessage('Microphone permission required - granting automatically...', 'system');
                        localStorage.setItem('voicebot-microphone-consent', 'granted');
                        localStorage.setItem('voicebot-microphone-consent-date', new Date().toISOString());
                        // Retry the call
                        setTimeout(() => widget.startVoiceCall(), 100);
                    });

                    // Volume monitoring
                    if (widget.voiceManager) {
                        widget.voiceManager.on('volume-level', (level) => {
                            updateVolumeDisplay(Math.floor(level * 10));
                        });
                    }

                    // Update widget info
                    function updateWidgetInfo() {
                        const sessionId = widget.getSessionId();
                        const info = \`
                            Chatbot ID: \${widget.getChatbotId()}<br>
                            Anonymous ID: \${widget.getAnonId()}<br>
                            Session ID: \${sessionId || 'Loading...'}<br>
                            Headless: \${widget.isHeadless()}<br>
                            Voice Enabled: \${widget.voiceManager ? 'Yes' : 'No'}
                        \`;
                        document.getElementById('widgetInfo').innerHTML = info;
                    }

                    updateWidgetInfo();

                    updateStatus(true);
                    updateVoiceStatus();
                    addMessage('Widget connected successfully!', 'system');

                    // Wait a bit for bootstrap to complete, then update info again
                    setTimeout(updateWidgetInfo, 500);

                } catch (error) {
                    addMessage(\`Connection failed: \${error.message}\`, 'system');
                    updateStatus(false);
                }
            }
        }

        // === INITIALIZATION ===
        // Enter key support
        document.getElementById('messageInput').addEventListener('keypress', (e) => {
            if (e.key === 'Enter') {
                sendMessage();
            }
        });

        // Try to connect every second until successful
        const connectInterval = setInterval(() => {
            if (!widget) {
                connectWidget();
            } else {
                clearInterval(connectInterval);
            }
        }, 1000);

        // Update voice status every second
        setInterval(() => {
            if (widget) updateVoiceStatus();
        }, 1000);

        // Initial setup
        updateStatus(false);
    </script>
</body>

</html>`;
    };

    // Copy to clipboard
    const copyToClipboard = async (text: string) => {
        try {
            await navigator.clipboard.writeText(text);
            success({
                title: "Copied!",
                description: "Code copied to clipboard",
                icon: Copy,
                duration: 3000
            });
        } catch (error) {
            console.error('Copy failed:', error);
        }
    };

    // Copy HMAC secret with tick feedback
    const copyHmacSecret = async () => {
        try {
            await navigator.clipboard.writeText(widgetData.hmacSecret || '');
            setIsHmacCopied(true);
            success({
                title: "Copied!",
                description: "HMAC secret copied to clipboard",
                icon: Copy,
                duration: 3000
            });
            setTimeout(() => setIsHmacCopied(false), 2000);
        } catch (error) {
            console.error('Copy failed:', error);
        }
    };

    // Toggle HMAC secret visibility
    const toggleHmacVisibility = () => {
        setIsHmacVisible(!isHmacVisible);
    };

    // Mask HMAC secret for security
    const getMaskedHmacSecret = () => {
        const secret = widgetData.hmacSecret || '';
        if (isHmacVisible) {
            return secret;
        }
        // Show first 4 and last 4 characters, mask the middle
        if (secret.length <= 8) {
            return '*'.repeat(secret.length);
        }
        return secret.slice(0, 4) + '*'.repeat(secret.length - 8) + secret.slice(-4);
    };

    // Download example file
    const downloadExampleFile = (type: 'simple' | 'headless') => {
        const content = generateCompleteHTMLFile(type);
        const filename = type === 'simple' ? 'widget-simple-example.html' : 'widget-headless-example.html';
        
        const blob = new Blob([content], { type: 'text/html' });
        const url = URL.createObjectURL(blob);
        
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        
        success({
            title: "Downloaded!",
            description: `${filename} downloaded successfully`,
            icon: Download,
            duration: 3000
        });
    };

    return (
        <div className="space-y-6">
            <Card className="shadow-sm">
                <CardHeader className="border-b bg-muted/20">
                    <div className="flex items-center gap-3">
                        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
                            <Code className="h-4 w-4 text-primary" />
                        </div>
                        <div className="flex-1 space-y-1">
                            <CardTitle>Integration Guide</CardTitle>
                            <CardDescription>Choose your integration method and copy the code to add this widget to your website</CardDescription>
                        </div>
                    </div>
                </CardHeader>
                <CardContent className="pt-6">
                    <Tabs defaultValue="simple" className="space-y-6" layoutId="integration-guide-tabs">
                        <TabsList className="h-11">
                            <TabsTrigger value="simple" className="gap-2 px-4">
                                <Globe className="h-4 w-4" />
                                Simple
                            </TabsTrigger>
                            <TabsTrigger value="headless" className="gap-2 px-4">
                                <Code className="h-4 w-4" />
                                Headless
                            </TabsTrigger>
                            <TabsTrigger value="workflow" className="gap-2 px-4">
                                <Workflow className="h-4 w-4" />
                                Chat Flow
                            </TabsTrigger>
                            <TabsTrigger value="info" className="gap-2 px-4">
                                <Zap className="h-4 w-4" />
                                API Info
                            </TabsTrigger>
                        </TabsList>

                        {/* Simple Integration Tab */}
                        <TabsContent value="simple" className="m-0">
                            <motion.div
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -10 }}
                                transition={{ duration: 0.2 }}
                            >
                                <div className="space-y-6">
                                <div className="flex items-center justify-between">
                                    <h4 className="text-base font-semibold">Add Script to Your Website</h4>
                                    <div className="flex gap-2">
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => downloadExampleFile('simple')}
                                        >
                                            <Download className="h-4 w-4 mr-2" />
                                            Download HTML
                                        </Button>
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => copyToClipboard(getSimpleIntegrationCode())}
                                        >
                                            <Copy className="h-4 w-4 mr-2" />
                                            Copy Code
                                        </Button>
                                    </div>
                                </div>
                                <div className="bg-muted/50 border p-4 rounded-lg font-mono text-sm overflow-x-auto max-h-96 overflow-y-auto">
                                    <pre className="text-xs">{getSimpleIntegrationCode()}</pre>
                                </div>
                                <p className="text-xs text-muted-foreground">
                                    Add this script tag to your application. The widget will load automatically with all your configured settings.
                                </p>
                            </div>
                            </motion.div>
                        </TabsContent>

                        {/* Headless Mode Tab */}
                        <TabsContent value="headless" className="m-0">
                            <motion.div
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -10 }}
                                transition={{ duration: 0.2 }}
                            >
                                <div className="space-y-6">
                                <div className="flex items-center justify-between">
                                    <h4 className="text-base font-semibold">Custom UI Integration</h4>
                                    <div className="flex gap-2">
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => downloadExampleFile('headless')}
                                        >
                                            <Download className="h-4 w-4 mr-2" />
                                            Download HTML
                                        </Button>
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => copyToClipboard(getHeadlessIntegrationCode())}
                                        >
                                            <Copy className="h-4 w-4 mr-2" />
                                            Copy Code
                                        </Button>
                                    </div>
                                </div>
                                <div className="bg-muted/50 border p-4 rounded-lg font-mono text-sm overflow-x-auto max-h-96 overflow-y-auto">
                                    <pre className="text-xs">{getHeadlessIntegrationCode()}</pre>
                                </div>
                                <p className="text-xs text-muted-foreground">
                                    Build completely custom chat interfaces while using the widget's API functionality. Perfect for custom designs and advanced integrations.
                                </p>
                            </div>
                            </motion.div>
                        </TabsContent>

                        {/* Chat Flow Tab */}
                        <TabsContent value="workflow" className="m-0">
                            <motion.div
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -10 }}
                                transition={{ duration: 0.2 }}
                            >
                                <div className="space-y-6">
                                {/* Header */}
                                <div>
                                    <h4 className="text-sm font-semibold mb-1">Workflow Integration</h4>
                                    <p className="text-xs text-muted-foreground">
                                        Handle chat messages with advanced processing, AI responses, and custom business logic.
                                    </p>
                                </div>

                                {/* Setup Steps */}
                                <div className="space-y-3">
                                    <div className="flex items-start gap-3">
                                        <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-primary/10 text-xs font-medium text-primary">1</div>
                                        <div className="flex-1 pt-0.5">
                                            <p className="text-sm font-medium">Import "Widget Chat Flow" Template</p>
                                            <p className="text-xs text-muted-foreground mt-0.5">Use the pre-built template from your flows library</p>
                                        </div>
                                    </div>
                                    <div className="flex items-start gap-3">
                                        <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-primary/10 text-xs font-medium text-primary">2</div>
                                        <div className="flex-1 pt-0.5">
                                            <p className="text-sm font-medium">Configure Webhook URL</p>
                                            <p className="text-xs text-muted-foreground mt-0.5">Set your workflow webhook URL in the Chat tab</p>
                                        </div>
                                    </div>
                                    <div className="flex items-start gap-3">
                                        <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-primary/10 text-xs font-medium text-primary">3</div>
                                        <div className="flex-1 pt-0.5">
                                            <p className="text-sm font-medium">Customize Flow Logic</p>
                                            <p className="text-xs text-muted-foreground mt-0.5">Adapt the workflow to your specific requirements</p>
                                        </div>
                                    </div>
                                </div>

                                {/* HMAC Secret */}
                                <div className="space-y-2">
                                    <div className="flex items-center gap-2">
                                        <Shield className="h-4 w-4 text-muted-foreground" />
                                        <h5 className="text-sm font-medium">HMAC Secret Key</h5>
                                    </div>
                                    <div className="rounded-lg border bg-muted/30 p-4 space-y-3">
                                        <p className="text-xs text-muted-foreground">
                                            Use this secret key to configure HMAC verification in your workflow.
                                        </p>
                                        <div className="flex items-center gap-2 rounded-md border bg-background p-2">
                                            <code className="flex-1 text-xs font-mono break-all">
                                                {getMaskedHmacSecret()}
                                            </code>
                                            <div className="flex gap-1 shrink-0">
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    className="h-7 w-7"
                                                    onClick={toggleHmacVisibility}
                                                    title={isHmacVisible ? "Hide secret" : "Show secret"}
                                                >
                                                    {isHmacVisible ? (
                                                        <EyeOff className="h-3.5 w-3.5" />
                                                    ) : (
                                                        <Eye className="h-3.5 w-3.5" />
                                                    )}
                                                </Button>
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    className="h-7 w-7"
                                                    onClick={copyHmacSecret}
                                                    title="Copy secret"
                                                >
                                                    {isHmacCopied ? (
                                                        <Check className="h-3.5 w-3.5" />
                                                    ) : (
                                                        <Copy className="h-3.5 w-3.5" />
                                                    )}
                                                </Button>
                                            </div>
                                        </div>
                                        <p className="text-xs text-muted-foreground">
                                            Keep this secret secure and only use it in server-side configurations.
                                        </p>
                                    </div>
                                </div>

                                {/* Verify & Normalize */}
                                <div className="space-y-2">
                                    <div className="flex items-center gap-2">
                                        <ShieldCheck className="h-4 w-4 text-muted-foreground" />
                                        <h5 className="text-sm font-medium">Verify & Normalize Piece</h5>
                                    </div>
                                    <div className="rounded-lg border bg-muted/30 p-4 space-y-3">
                                        <p className="text-xs text-muted-foreground">
                                            Security validation and data normalization for incoming messages.
                                        </p>
                                        
                                        {/* Key Features */}
                                        <div className="space-y-2">
                                            <p className="text-xs font-medium">Key Features</p>
                                            <ul className="space-y-1 text-xs text-muted-foreground">
                                                <li className="flex items-start gap-2">
                                                    <span className="text-primary mt-0.5">•</span>
                                                    <span>HMAC signature verification with chatbot-specific secrets</span>
                                                </li>
                                                <li className="flex items-start gap-2">
                                                    <span className="text-primary mt-0.5">•</span>
                                                    <span>Timestamp validation to prevent replay attacks</span>
                                                </li>
                                                <li className="flex items-start gap-2">
                                                    <span className="text-primary mt-0.5">•</span>
                                                    <span>Payload normalization and session context</span>
                                                </li>
                                            </ul>
                                        </div>

                                        {/* Configuration Example */}
                                        <div className="space-y-1.5">
                                            <p className="text-xs font-medium">Configuration</p>
                                            <div className="rounded-md border bg-background p-3">
                                                <pre className="text-xs font-mono overflow-x-auto">{JSON.stringify({
                                                    "signature": "{{ trigger.headers['x-hmac-signature'] }}",
                                                    "payload": "{{ trigger.body }}",
                                                    "hmacSecret": widgetData.hmacSecret,
                                                    "maxTimeDelta": 300,
                                                    "strictMode": true
                                                }, null, 2)}</pre>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Workflow Structure */}
                                <div className="space-y-2">
                                    <div className="flex items-center gap-2">
                                        <Workflow className="h-4 w-4 text-muted-foreground" />
                                        <h5 className="text-sm font-medium">Typical Workflow Structure</h5>
                                    </div>
                                    <div className="rounded-lg border bg-muted/30 p-4">
                                        <div className="space-y-2 text-xs">
                                            <div className="flex items-center gap-2">
                                                <div className="flex h-5 w-5 shrink-0 items-center justify-center rounded bg-primary/20 text-xs font-medium text-primary">1</div>
                                                <span className="text-muted-foreground">Webhook Trigger</span>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <div className="flex h-5 w-5 shrink-0 items-center justify-center rounded bg-primary/20 text-xs font-medium text-primary">2</div>
                                                <span className="text-muted-foreground">Verify & Normalize</span>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <div className="flex h-5 w-5 shrink-0 items-center justify-center rounded bg-primary/20 text-xs font-medium text-primary">3</div>
                                                <span className="text-muted-foreground">Router (session logic)</span>
                                            </div>
                                            <div className="flex items-center gap-2 ml-7">
                                                <div className="flex h-5 w-5 shrink-0 items-center justify-center rounded bg-primary/10 text-xs font-medium text-primary">4a</div>
                                                <span className="text-muted-foreground">New Session → Create</span>
                                            </div>
                                            <div className="flex items-center gap-2 ml-7">
                                                <div className="flex h-5 w-5 shrink-0 items-center justify-center rounded bg-primary/10 text-xs font-medium text-primary">4b</div>
                                                <span className="text-muted-foreground">Existing Session → Chat</span>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <div className="flex h-5 w-5 shrink-0 items-center justify-center rounded bg-primary/20 text-xs font-medium text-primary">5</div>
                                                <span className="text-muted-foreground">Format Response</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            </motion.div>
                        </TabsContent>

                        {/* API Info Tab */}
                        <TabsContent value="info" className="m-0">
                            <motion.div
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -10 }}
                                transition={{ duration: 0.2 }}
                            >
                                <div className="space-y-6">
                                {/* Header */}
                                <div>
                                    <h4 className="text-sm font-semibold mb-1">API & Technical Information</h4>
                                    <p className="text-xs text-muted-foreground">
                                        Technical details about security, sessions, and data handling.
                                    </p>
                                </div>

                                {/* Security & Data Storage */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div className="space-y-2">
                                        <div className="flex items-center gap-2">
                                            <Shield className="h-4 w-4 text-muted-foreground" />
                                            <h5 className="text-sm font-medium">Security & Authentication</h5>
                                        </div>
                                        <div className="rounded-lg border bg-muted/30 p-4">
                                            <ul className="space-y-1.5 text-xs text-muted-foreground">
                                                <li className="flex items-start gap-2">
                                                    <span className="text-primary mt-0.5">•</span>
                                                    <span>JWT authentication with RS256</span>
                                                </li>
                                                <li className="flex items-start gap-2">
                                                    <span className="text-primary mt-0.5">•</span>
                                                    <span>1-hour token expiry with auto-refresh</span>
                                                </li>
                                                <li className="flex items-start gap-2">
                                                    <span className="text-primary mt-0.5">•</span>
                                                    <span>Origin validation with allowlists</span>
                                                </li>
                                                <li className="flex items-start gap-2">
                                                    <span className="text-primary mt-0.5">•</span>
                                                    <span>HMAC-SHA256 signatures</span>
                                                </li>
                                                <li className="flex items-start gap-2">
                                                    <span className="text-primary mt-0.5">•</span>
                                                    <span>Rate limiting for API protection</span>
                                                </li>
                                            </ul>
                                        </div>
                                    </div>
                                    <div className="space-y-2">
                                        <div className="flex items-center gap-2">
                                            <Database className="h-4 w-4 text-muted-foreground" />
                                            <h5 className="text-sm font-medium">Data Storage & Sessions</h5>
                                        </div>
                                        <div className="rounded-lg border bg-muted/30 p-4">
                                            <ul className="space-y-1.5 text-xs text-muted-foreground">
                                                <li className="flex items-start gap-2">
                                                    <span className="text-primary mt-0.5">•</span>
                                                    <span>Local storage (24-hour TTL)</span>
                                                </li>
                                                <li className="flex items-start gap-2">
                                                    <span className="text-primary mt-0.5">•</span>
                                                    <span>Max 50 messages per session</span>
                                                </li>
                                                <li className="flex items-start gap-2">
                                                    <span className="text-primary mt-0.5">•</span>
                                                    <span>Hybrid session management</span>
                                                </li>
                                                <li className="flex items-start gap-2">
                                                    <span className="text-primary mt-0.5">•</span>
                                                    <span>Cross-tab synchronization</span>
                                                </li>
                                                <li className="flex items-start gap-2">
                                                    <span className="text-primary mt-0.5">•</span>
                                                    <span>Session persistence on refresh</span>
                                                </li>
                                            </ul>
                                        </div>
                                    </div>
                                </div>

                                {/* API Methods */}
                                <div className="space-y-2">
                                    <div className="flex items-center gap-2">
                                        <Code className="h-4 w-4 text-muted-foreground" />
                                        <h5 className="text-sm font-medium">Key API Methods</h5>
                                    </div>
                                    <div className="rounded-lg border bg-muted/30 p-4">
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div className="space-y-2">
                                                <p className="text-xs font-medium">Chat Methods</p>
                                                <ul className="space-y-1 text-xs font-mono text-muted-foreground">
                                                    <li>VoicebotSDK.send()</li>
                                                    <li>VoicebotSDK.getMessages()</li>
                                                    <li>VoicebotSDK.clearHistory()</li>
                                                    <li>VoicebotSDK.open()</li>
                                                    <li>VoicebotSDK.close()</li>
                                                </ul>
                                            </div>
                                            <div className="space-y-2">
                                                <p className="text-xs font-medium">Voice Methods</p>
                                                <ul className="space-y-1 text-xs font-mono text-muted-foreground">
                                                    <li>startVoiceCall()</li>
                                                    <li>endVoiceCall()</li>
                                                    <li>isVoiceCallActive()</li>
                                                    <li>setVoiceMuted()</li>
                                                    <li>isVoiceMuted()</li>
                                                    <li>getVoiceCallDuration()</li>
                                                </ul>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Event Listeners */}
                                <div className="space-y-2">
                                    <div className="flex items-center gap-2">
                                        <Radio className="h-4 w-4 text-muted-foreground" />
                                        <h5 className="text-sm font-medium">Event Listeners</h5>
                                    </div>
                                    <div className="rounded-lg border bg-muted/30 p-4">
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div className="space-y-2">
                                                <p className="text-xs font-medium">Chat Events</p>
                                                <ul className="space-y-1 text-xs font-mono text-muted-foreground">
                                                    <li>'message'</li>
                                                    <li>'open'</li>
                                                    <li>'close'</li>
                                                    <li>'error'</li>
                                                </ul>
                                            </div>
                                            <div className="space-y-2">
                                                <p className="text-xs font-medium">Voice Events</p>
                                                <ul className="space-y-1 text-xs font-mono text-muted-foreground">
                                                    <li>'voice-started'</li>
                                                    <li>'voice-ended'</li>
                                                    <li>'voice-error'</li>
                                                    <li>'voice-speech-start'</li>
                                                    <li>'voice-speech-end'</li>
                                                    <li>'voice-volume'</li>
                                                    <li>'microphone-consent-required'</li>
                                                </ul>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Performance */}
                                <div className="space-y-2">
                                    <div className="flex items-center gap-2">
                                        <Zap className="h-4 w-4 text-muted-foreground" />
                                        <h5 className="text-sm font-medium">Performance & Compatibility</h5>
                                    </div>
                                    <div className="rounded-lg border bg-muted/30 p-4">
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div className="space-y-2">
                                                <p className="text-xs font-medium">Bundle Size</p>
                                                <ul className="space-y-1 text-xs text-muted-foreground">
                                                    <li className="flex items-start gap-2">
                                                        <span className="text-primary mt-0.5">•</span>
                                                        <span>Widget: ~55KB (minified)</span>
                                                    </li>
                                                    <li className="flex items-start gap-2">
                                                        <span className="text-primary mt-0.5">•</span>
                                                        <span>Voice: ~2MB (lazy-loaded)</span>
                                                    </li>
                                                    <li className="flex items-start gap-2">
                                                        <span className="text-primary mt-0.5">•</span>
                                                        <span>Zero dependencies for chat</span>
                                                    </li>
                                                </ul>
                                            </div>
                                            <div className="space-y-2">
                                                <p className="text-xs font-medium">Browser Support</p>
                                                <ul className="space-y-1 text-xs text-muted-foreground">
                                                    <li className="flex items-start gap-2">
                                                        <span className="text-primary mt-0.5">•</span>
                                                        <span>Chrome 60+, Firefox 60+</span>
                                                    </li>
                                                    <li className="flex items-start gap-2">
                                                        <span className="text-primary mt-0.5">•</span>
                                                        <span>Safari 12+, Edge 79+</span>
                                                    </li>
                                                    <li className="flex items-start gap-2">
                                                        <span className="text-primary mt-0.5">•</span>
                                                        <span>Mobile browsers supported</span>
                                                    </li>
                                                </ul>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            </motion.div>
                        </TabsContent>
                    </Tabs>
                </CardContent>
            </Card>
        </div>
    );
}
